import {useCallback} from 'react';
import {useIdpClient} from './useIdpClient';
import Auth from './Auth';
import {getCurrentFirmId} from '../lib/picos/currentLogin';
import {confirmError} from '../ui/Confirm';
import {clearAccountLocalStorage} from '../lib/accountLocalStorage';
import {tabSession} from './session';
import {markSignedOut} from './signedOutMarker';
import {HOME_PATH} from './sessionNotice';

/** End this document's session; shared provider SSO and other tabs remain untouched. */
export function useLogout() {
    const {logout} = useIdpClient();
    return useCallback(async (isPortal?: boolean, discardUnsaved = false, clearPreferences = false): Promise<boolean> => {
        const firmId = getCurrentFirmId();
        const owner = tabSession.getIdentity();
        const signedOut = await Auth.logoutCleanup(isPortal, discardUnsaved);
        if (!signedOut) return false;
        try {
            signedOut.assertCurrent();
            if (clearPreferences && owner) clearAccountLocalStorage(owner);
            // Only the tab is signed out, so the next sign-in must ask for credentials.
            if (!markSignedOut()) {
                confirmError('Sign out incomplete', 'This tab is locked, but the browser would not store the sign-out, so the next sign-in may not ask for your password. Free browser storage space, then sign out again.');
                return false;
            }
            await logout({localOnly: true});
            signedOut.assertCurrent();
            // A new document also releases account-bound Redux, component caches and workers.
            window.location.replace(isPortal && firmId ? '/portal?firmId=' + encodeURIComponent(firmId) : HOME_PATH);
        }
        catch {
            if (signedOut.signal.aborted) return false;
            confirmError('Sign out incomplete', clearPreferences
                ? 'This tab is locked, but its preferences may not have finished clearing. No other account was selected for clearing. Reload this tab to finish signing out.'
                : 'This tab is locked. Reload it to finish signing out.');
            return false;
        }
        return true;
    }, [logout]);
}
